// Лента всех чеков (Алексей 27.10 #2962): бесконечная прокрутка, клик — раскрыть позиции.
// Следующие шаги того же запроса — разговор у кассы по чеку, «сам назвал / предложил бариста»,
// контекст момента (посадка, очередь, выкладка, столы) — встраиваются в раскрытие.
import React, { useEffect, useRef, useState } from 'react';
import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import useSWRInfinite from 'swr/infinite';
import { AlertTriangle, ChevronDown, Loader2, MessageSquareText, PackageX, ShoppingBasket, Users, X } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Card, CardContent } from '@/components/ui/card';
import { listReceipts, type ReceiptItemSource, type ReceiptRowDto, type ReceiptsPageDto } from '@/api/receipts';
import { money } from '@/lib/finance';
import { cn } from '@/lib/utils';
import { Tooltip } from '@/components/ui/tooltip';

const rub = (n: number) => money(`${(Math.round(n * 100) / 100).toLocaleString('ru-RU')} ₽`);
const qtyLabel = (q: number) => (Number.isInteger(q) ? String(q) : q.toLocaleString('ru-RU'));
const dayLabel = (iso: string) => new Date(iso).toLocaleDateString('ru-RU', { weekday: 'short', day: 'numeric', month: 'long' });
const timeLabel = (iso: string) => iso.slice(11, 16); // время точки — уже в смещении зоны

export function ReceiptsFeed({ zoneId, from, to, operator, onOperator }: {
  zoneId: string;
  from: string;
  to: string;
  operator: string;
  onOperator: (name: string) => void;
}) {
  const getKey = (i: number, prev: ReceiptsPageDto | null) => {
    if (prev && !prev.next_cursor) return null;
    return ['receipts:list', zoneId, from, to, operator, i === 0 ? null : prev!.next_cursor] as const;
  };
  const { data, size, setSize, isLoading, isValidating, error } = useSWRInfinite(
    getKey,
    ([, z, f, t, op, cursor]) => listReceipts(z, { cursor, operator: op, date_from: f, date_to: t }).then((r) => r.data),
    { revalidateFirstPage: false, revalidateOnFocus: false },
  );
  const rows = (data ?? []).flatMap((p) => p.results);
  const clock = Object.assign({}, ...(data ?? []).map((p) => p.clock ?? {})) as NonNullable<ReceiptsPageDto['clock']>;
  const done = !!data && !data[data.length - 1]?.next_cursor;
  const loadingMore = isValidating && !!data && size > data.length;
  const sentinel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sentinel.current;
    if (!el || done) return;
    const io = new IntersectionObserver((es) => {
      if (es.some((e) => e.isIntersecting) && !isValidating) void setSize((s) => s + 1);
    }, { rootMargin: '600px' });
    io.observe(el);
    return () => io.disconnect();
  }, [done, isValidating, setSize]);

  let lastDay = '';
  return (
    <Card>
      <CardContent className="px-3 pb-3 pt-3">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <div className="text-xs text-muted-foreground">Все чеки</div>
          {operator && (
            <button type="button" onClick={() => onOperator('')}
              className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs hover:bg-muted">
              Кассир: {operator} <X className="size-3" />
            </button>
          )}
        </div>
        {error && !data && <ServerStatusNotice variant="card" error={error} />}
        {isLoading && !data && <div className="h-40 animate-pulse rounded-lg bg-muted" />}
        {data && rows.length === 0 && <div className="py-4 text-sm text-muted-foreground">Чеков за период нет</div>}
        <div className="divide-y divide-border/50">
          {rows.map((r) => {
            const day = r.at.slice(0, 10);
            const ck = clock[day];
            const head = day !== lastDay ? (
              <div key={`d${day}`} className="sticky top-0 z-[1] flex flex-wrap items-baseline gap-x-3 bg-card pb-1 pt-3 text-xs">
                <span className="font-medium text-muted-foreground">{dayLabel(r.at)}</span>
                {ck?.warn && ck.offset_s != null && (
                  <Tooltip content={`По ${ck.n} чекам, где кассир назвал сумму: время чека минус время оплаты в записи`}><span className="inline-flex items-center gap-1 text-orange-600 dark:text-orange-400">
                    <AlertTriangle className="size-3.5" />
                    Часы кассы {ck.offset_s < 0 ? 'отстают' : 'спешат'} на ~{Math.round(Math.abs(ck.offset_s) / 60)} мин
                  </span></Tooltip>
                )}
              </div>
            ) : null;
            lastDay = day;
            return (
              <React.Fragment key={r.id}>
                {head}
                <ReceiptRow r={r} onOperator={onOperator} zoneId={zoneId} />
              </React.Fragment>
            );
          })}
        </div>
        <div ref={sentinel} />
        {(loadingMore || (isValidating && !done && rows.length > 0)) && (
          <div className="flex items-center justify-center gap-1.5 py-3 text-xs text-muted-foreground"><Loader2 className="size-3.5 animate-spin" /> Загружаем…</div>
        )}
        {done && rows.length > 0 && <div className="py-3 text-center text-xs text-muted-foreground">Это все чеки за период</div>}
      </CardContent>
    </Card>
  );
}

// Разбор чека (Алексей 29.10 #3229: «вывести поярче, когда продали позицию, а когда отказались, и пометки
// низкая выкладка и т.п. тоже внести в чек»): в строке — цветные метки итога, в раскрытии — позиции с полосой
// по источнику, ниже «предложил — не взяли» и «не оказалось», и момент: витрина, очередь.
function ReceiptRow({ r, onOperator, zoneId }: { r: ReceiptRowDto; onOperator: (name: string) => void; zoneId: string }) {
  const [open, setOpen] = useState(false);
  const names = r.items.map((it) => it.name).join(', ');
  const sold = r.items.filter((it) => it.src && (it.src.label === 'upsold' || it.src.label === 'recommended' || it.src.label === 'after_ask')).length;
  const declined = r.declined ?? [];
  // Отказ от конкретной позиции — красным; общее «что-то ещё?» — серым, это не отказ от товара (#3465).
  const declinedItem = declined.filter((d) => !d.generic);
  const declinedAsk = declined.filter((d) => d.generic);
  const missing = r.missing ?? [];
  const marks = r.marks ?? [];
  const low = marks.filter((m): m is Extract<typeof m, { kind: 'low_layout' }> => m.kind === 'low_layout');
  // Цвет «мало товара» — по степени недостатка самой просевшей витрины (#3469): чуть-чуть — серым.
  const lowTone = low.some((m) => m.level === 'strong') ? 'bad' : low.some((m) => m.level === 'medium') ? 'warn' : 'muted';
  const queue = marks.find((m): m is Extract<typeof m, { kind: 'queue' }> => m.kind === 'queue');
  return (
    <div className={cn('py-1.5', r.op === 'return' && 'text-destructive')}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-3 text-left text-sm">
        <span className="w-11 shrink-0 tabular-nums text-muted-foreground">{timeLabel(r.at)}</span>
        <span className="flex min-w-0 flex-1 items-center gap-1.5">
          <span className="min-w-0 truncate">{r.op === 'return' && 'Возврат · '}{names || '—'}</span>
          {sold > 0 && <Badge tone="good">+{sold} допродал{sold > 1 ? 'а' : ''}</Badge>}
          {declinedItem.length > 0 && <Badge tone="bad">не взяли{declinedItem.length > 1 ? ` ×${declinedItem.length}` : ''}</Badge>}
          {declinedAsk.length > 0 && <Badge tone="muted">что-то ещё — отказались</Badge>}
          {missing.length > 0 && <Badge tone="warn">нет в наличии</Badge>}
          {low.length > 0 && <Badge tone={lowTone} icon={<ShoppingBasket className="size-3" />}>мало товара: {low.map((m) => m.rack).join(', ')}</Badge>}
          {queue && <Badge tone="muted" icon={<Users className="size-3" />}>очередь {queue.people}</Badge>}
        </span>
        {r.conversation && (
          <MessageSquareText className="size-3.5 shrink-0 text-muted-foreground" aria-label="Есть разговор у кассы" />
        )}
        <span className="shrink-0 font-medium tabular-nums">{rub(r.total)}</span>
        <ChevronDown className={cn('size-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="ml-14 mt-1.5 space-y-1 rounded-lg bg-muted/40 px-3 py-2 text-sm">
          {r.items.map((it, i) => {
            const v = it.src ? SRC[it.src.label] : null;
            return (
              <div key={i} className={cn('-mx-1.5 flex items-baseline gap-2 rounded-md border-l-[3px] border-transparent px-1.5 py-0.5', v?.row)}>
                <span className="min-w-0 flex-1">
                  <span className={cn(v?.strong && 'font-medium')}>{it.name}</span>
                  {it.src && <SourceChip label={it.src.label} quote={it.src.quote} />}
                  {it.src?.quote && v?.strong && <span className="ml-1.5 text-xs italic text-muted-foreground">«{it.src.quote}»</span>}
                </span>
                <span className="shrink-0 tabular-nums text-muted-foreground">{qtyLabel(it.qty)} × {rub(it.price)}</span>
                <span className="w-24 shrink-0 text-right tabular-nums">{rub(it.sum)}</span>
              </div>
            );
          })}
          {declinedItem.map((d, i) => (
            <div key={`d${i}`} className="-mx-1.5 flex items-baseline gap-2 rounded-md border-l-[3px] border-destructive bg-destructive/10 px-1.5 py-0.5">
              <X className="size-3.5 shrink-0 self-center text-destructive" />
              <span className="min-w-0 flex-1">
                <span className="font-medium text-destructive">Предложил — не взяли:</span> {d.what}
                {d.quote && <span className="ml-1.5 text-xs italic text-muted-foreground">«{d.quote}»</span>}
              </span>
            </div>
          ))}
          {declinedAsk.map((d, i) => (
            <div key={`a${i}`} className="-mx-1.5 flex items-baseline gap-2 rounded-md border-l-[3px] border-border px-1.5 py-0.5 text-muted-foreground">
              <span className="min-w-0 flex-1">
                Спросил «что-то ещё?» — отказались
                {d.quote && <span className="ml-1.5 text-xs italic">«{d.quote}»</span>}
              </span>
            </div>
          ))}
          {missing.map((m, i) => (
            <div key={`m${i}`} className="-mx-1.5 flex items-baseline gap-2 rounded-md border-l-[3px] border-orange-500 bg-orange-500/10 px-1.5 py-0.5">
              <PackageX className="size-3.5 shrink-0 self-center text-orange-600 dark:text-orange-400" />
              <span className="min-w-0 flex-1">
                <span className="font-medium text-orange-700 dark:text-orange-300">{MISSING[m.reason] ?? 'Не оказалось'}:</span> {m.what || '—'}
                {(m.quote || m.answer) && <span className="ml-1.5 text-xs italic text-muted-foreground">«{m.quote || m.answer}»</span>}
              </span>
            </div>
          ))}
          {(low.length > 0 || queue) && (
            <div className="flex flex-wrap gap-1.5 pt-0.5">
              {low.map((m, i) => (
                <span key={i} className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs',
                  m.level === 'strong' ? 'bg-destructive/15 text-destructive'
                    : m.level === 'medium' ? 'bg-orange-500/15 text-orange-700 dark:text-orange-300'
                      : 'bg-muted text-muted-foreground')}>
                  <ShoppingBasket className="size-3" /> В момент чека мало товара: {m.rack}
                  {m.deficit != null && <span className="opacity-70">· ниже нормы на {Math.round(m.deficit * 100)} %</span>}
                </span>
              ))}
              {queue && (
                <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                  <Users className="size-3" /> Очередь {queue.people} чел.
                </span>
              )}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-border/50 pt-1.5 text-xs text-muted-foreground">
            {r.operator && (
              <button type="button" className="underline-offset-2 hover:underline" onClick={() => onOperator(r.operator)}>
                Кассир: {r.operator}
              </button>
            )}
            <span>{r.card && r.cash ? 'карта + наличные' : r.card ? 'карта' : r.cash ? 'наличные' : ''}</span>
            <span>чек № {r.doc}{r.shift != null ? ` · смена ${r.shift}` : ''}</span>
          </div>
          {r.conversation && <ConversationBlock c={r.conversation} zoneId={zoneId} />}
        </div>
      )}
    </div>
  );
}

const MISSING: Record<string, string> = {
  sold_out: 'Кончилось', not_delivered: 'Не привезли', absent: 'Нет в наличии', not_sold_here: 'Не продаём',
};

function Badge({ tone, icon, children }: { tone: 'good' | 'bad' | 'warn' | 'muted'; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <span className={cn('inline-flex shrink-0 items-center gap-0.5 rounded-full px-1.5 py-px text-[11px] font-medium',
      tone === 'good' && 'bg-success/15 text-success',
      tone === 'bad' && 'bg-destructive/15 text-destructive',
      tone === 'warn' && 'bg-orange-500/15 text-orange-700 dark:text-orange-300',
      tone === 'muted' && 'bg-muted text-muted-foreground')}>
      {icon}{children}
    </span>
  );
}

const SRC: Record<ReceiptItemSource, { text: string; cls: string; row?: string; strong?: boolean }> = {
  self: { text: 'сам назвал', cls: 'bg-muted text-muted-foreground' },
  after_ask: { text: 'после «что-то ещё?»', cls: 'bg-primary/15 text-primary', row: 'border-primary bg-primary/5', strong: true },
  recommended: { text: 'порекомендовал', cls: 'bg-success/20 text-success', row: 'border-success bg-success/10', strong: true },
  upsold: { text: 'допродал', cls: 'bg-success/20 text-success', row: 'border-success bg-success/10', strong: true },
};

function SourceChip({ label, quote }: { label: ReceiptItemSource; quote: string }) {
  const v = SRC[label];
  return (
    <Tooltip content={quote ? `«${quote}»` : undefined}><span className={cn('ml-1.5 inline-block rounded-full px-1.5 py-px align-middle text-[11px]', v.cls)}>{v.text}</span></Tooltip>
  );
}

/** Разговор у кассы по этому чеку (#2962): кратко + диалог по клику, ссылка на запись. */
function ConversationBlock({ c, zoneId }: { c: NonNullable<ReceiptRowDto['conversation']>; zoneId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-border/50 pt-1.5 text-xs">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <MessageSquareText className="size-3.5 self-center text-muted-foreground" />
        <span className="text-muted-foreground">Разговор в {c.started_at.slice(11, 16)}{c.match === 'medium' ? ' (по времени)' : ''}:</span>
        <span className="min-w-0 flex-1">{c.summary}</span>
      </div>
      <div className="mt-1 flex gap-3">
        <button type="button" className="text-primary underline-offset-2 hover:underline" onClick={() => setOpen((o) => !o)}>
          {open ? 'Скрыть диалог' : 'Показать диалог'}
        </button>
        {/* Разговор — на вкладке «Разговоры» за этот день, а не в настройках речи (Алексей 03.11 #3544) */}
        <Link to={`/management/analytics?tab=conversations&zone=${zoneId}&from=${c.started_at.slice(0, 10)}&to=${c.started_at.slice(0, 10)}&conv=${c.id}`}
          className="text-primary underline-offset-2 hover:underline">Послушать</Link>
      </div>
      {open && <pre className="mt-1.5 max-h-72 overflow-auto whitespace-pre-wrap rounded-md bg-background/60 p-2 font-sans text-xs leading-relaxed">{c.dialogue}</pre>}
    </div>
  );
}
